// ══════════════════════════════════════════════════════════════════════════════
// BrowsePage.js — Browse Gita by Theme
// Views: grid → verse list → full-page verse detail (independent of daily shloka)
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// ── Theme Grid ────────────────────────────────────────────────────────────────

function ThemeGrid({ onSelect, lang }) {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/themes`)
      .then(r => r.json())
      .then(d => { setThemes(d.themes || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ color: "var(--text-dim)", padding: "48px", textAlign: "center" }}>
      🕉️ {lang === "hi" ? "विषय लोड हो रहे हैं..." : lang === "hi_roman" ? "Vishay load ho rahe hain..." : lang === "ta" ? "தீம்கள் ஏற்றுகிறது..." : lang === "ta_roman" ? "Themes aetrugiraadhu..." : "Loading themes..."}
    </div>
  );

  return (
    <div>
      <div className="section-title">
        {lang === "hi" ? "विषय के अनुसार देखो" : lang === "hi_roman" ? "Vishay ke Anusaar Dekho" : "Browse by Theme"}
      </div>
      <div className="section-sub">
        {lang === "hi" ? "गीता के छह द्वार — ज्ञान के अलग-अलग पहलू"
         : lang === "hi_roman" ? "Gita ke chhe dwaar — gyaan ke alag-alag pahlu"
         : "Six doors into the Gita — each a different facet of its wisdom"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "16px" }}>
        {themes.map(theme => (
          <ThemeCard key={theme.id} theme={theme} lang={lang} onClick={() => onSelect(theme)} />
        ))}
      </div>
    </div>
  );
}

function ThemeCard({ theme, lang, onClick }) {
  const [hovered, setHovered] = useState(false);
  const loc = lang === "hi"       ? THEME_HI[theme.id]
            : lang === "hi_roman" ? THEME_HI_ROMAN[theme.id]
            : lang === "ta_roman" ? THEME_TA_ROMAN[theme.id]
            : lang === "ta"       ? THEME_TA[theme.id]
            : null;
  const title   = loc?.title    || theme.title;
  const subtitle= loc?.subtitle || theme.subtitle;
  const desc    = loc?.desc     || theme.description;
  const explore = loc?.explore  || "Explore verses →";

  return (
    <div onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? `linear-gradient(145deg, var(--bg2), ${theme.color}14)` : "linear-gradient(145deg, var(--bg2), var(--bg3))",
        border: `1px solid ${hovered ? theme.color + "60" : "var(--border)"}`,
        borderRadius: "18px", padding: "26px 24px 22px", cursor: "pointer",
        transition: "all 0.25s", position: "relative", overflow: "hidden",
        boxShadow: hovered ? `0 8px 32px ${theme.color}20` : "0 2px 12px rgba(201,149,58,0.07)",
      }}
    >
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${theme.color}, ${theme.color}55, transparent)`,
      }} />
      <div style={{
        position: "absolute", top: -30, right: -30, width: 140, height: 140, borderRadius: "50%",
        background: `radial-gradient(circle, ${theme.color}18 0%, transparent 70%)`,
        pointerEvents: "none", transition: "opacity 0.25s", opacity: hovered ? 1 : 0,
      }} />
      <div style={{ fontSize: "2.4rem", marginBottom: "14px" }}>{theme.icon}</div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.3rem", color: hovered ? darkColor(theme) : "var(--saffron)", marginBottom: "8px", transition: "color 0.25s" }}>
        {title}
      </div>
      <div style={{ fontSize: "1.05rem", color: "var(--text-dim)", marginBottom: "12px", lineHeight: 1.5 }}>
        {subtitle}
      </div>
      <div style={{ fontSize: "1.15rem", color: "var(--text)", lineHeight: "1.7" }}>{desc}</div>
      <div style={{ marginTop: "18px", fontSize: "1rem", color: hovered ? darkColor(theme) : "var(--text-dim)", transition: "color 0.25s", fontWeight: hovered ? 600 : 400 }}>
        {explore}
      </div>
    </div>
  );
}

// ── Theme Verse List ──────────────────────────────────────────────────────────

function ThemeVerseList({ themeId, onBack, onOpenVerse, lang }) {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/themes/${themeId}`)
      .then(r => r.json())
      .then(d => { setTheme(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [themeId]);

  if (loading) return (
    <div style={{ color: "var(--text-dim)", padding: "48px", textAlign: "center" }}>
      🕉️ {lang === "hi" ? "श्लोक लोड हो रहे हैं..." : lang === "hi_roman" ? "Shlok load ho rahe hain..." : lang === "ta" ? "வசனங்கள் ஏற்றுகிறது..." : lang === "ta_roman" ? "Vasanangal aetrugiraadhu..." : "Loading verses..."}
    </div>
  );

  if (!theme || theme.error) return <div style={{ color: "var(--text-dim)", padding: "40px" }}>{lang === "hi" ? "विषय नहीं मिला।" : lang === "hi_roman" ? "Vishay nahi mila." : "Theme not found."}</div>;

  return (
    <div>
      <button onClick={onBack} style={{
        background: "none", border: "none", cursor: "pointer",
        color: "var(--text-dim)", fontFamily: "'Inter',sans-serif",
        fontSize: "1rem", display: "flex", alignItems: "center", gap: "6px",
        padding: "0", marginBottom: "20px",
      }}>
        ← {lang === "hi" ? "सभी विषय" : lang === "hi_roman" ? "Sabhi Vishay" : lang === "ta" || lang === "ta_roman" ? "எல்லா தீம்கள்" : "All Themes"}
      </button>

      {(() => {
        const localTitle = lang === "hi" ? THEME_HI[theme.id]?.title
                         : lang === "hi_roman" ? THEME_HI_ROMAN[theme.id]?.title
                         : lang === "ta" ? THEME_TA[theme.id]?.title
                         : lang === "ta_roman" ? THEME_TA_ROMAN[theme.id]?.title
                         : null;
        const localDesc  = lang === "hi" ? THEME_HI[theme.id]?.desc
                         : lang === "hi_roman" ? THEME_HI_ROMAN[theme.id]?.desc
                         : lang === "ta" ? THEME_TA[theme.id]?.desc
                         : lang === "ta_roman" ? THEME_TA_ROMAN[theme.id]?.desc
                         : null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "8px" }}>
            <span style={{ fontSize: "2.4rem" }}>{theme.icon}</span>
            <div>
              <div className="section-title" style={{ marginBottom: "2px" }}>{localTitle || theme.title}</div>
              <div className="section-sub" style={{ marginBottom: 0 }}>{localDesc || theme.description}</div>
            </div>
          </div>
        );
      })()}

      <div style={{ height: "2px", background: `linear-gradient(90deg, ${theme.color}88, transparent)`, borderRadius: "2px", marginBottom: "28px" }} />

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {theme.verses.map((v, i) => (
          <div key={`${v.chapter}.${v.verse}`}
            onClick={() => onOpenVerse(theme, i)}
            style={{
              background: "linear-gradient(135deg, var(--bg2), var(--bg3))",
              border: "1px solid var(--border)",
              borderRadius: "12px", padding: "16px 20px",
              cursor: "pointer", display: "flex", alignItems: "center",
              gap: "14px", transition: "border-color 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = theme.color + "55"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
          >
            <div style={{
              background: `${theme.color}18`, border: `1px solid ${theme.color}44`,
              borderRadius: "8px", padding: "5px 12px",
              fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1rem",
              color: darkColor(theme), flexShrink: 0,
            }}>
              BG {v.chapter}.{v.verse}
            </div>
            <div style={{ fontSize: "1.15rem", lineHeight: "1.6", flex: 1, overflow: "hidden",
              display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
              color: "var(--text)", fontStyle: "normal",
            }}>
              {(lang === "hi" || lang === "hi_roman")
                ? (v.hi_preview || (v.meaning ? v.meaning.split(/[.!?]/)[0].trim() + "." : ""))
                : (v.meaning ? v.meaning.split(/[.!?]/)[0].trim() + "." : "")}
            </div>
            <span style={{ color: "var(--text-dim)", fontSize: "1.1rem", flexShrink: 0 }}>→</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Full-Page Verse Detail ────────────────────────────────────────────────────

function ThemeVerseDetail({ theme, verseIdx, onBack, onNext, onPrev, lang }) {
  const verse = theme.verses[verseIdx];
  const [commentary, setCommentary] = useState(null);
  const [loadingComm, setLoadingComm] = useState(false);

  useEffect(() => {
    if (!verse) return;
    setCommentary(null);
    setLoadingComm(true);
    fetch(`${API}/shloka/commentary/${verse.chapter}/${verse.verse}?theme=${theme.id}&lang=${lang || "en"}`)
      .then(r => r.json())
      .then(d => { setCommentary(d); setLoadingComm(false); })
      .catch(() => setLoadingComm(false));
  }, [verse?.chapter, verse?.verse, lang]);

  if (!verse) return null;

  const total = theme.verses.length;

  return (
    <div>
      {/* Back + progress */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "28px" }}>
        <button onClick={onBack} style={{
          background: "none", border: "none", cursor: "pointer",
          color: "var(--text-dim)", fontFamily: "'Inter',sans-serif",
          fontSize: "1rem", display: "flex", alignItems: "center", gap: "6px", padding: "0",
        }}>
          ← {(lang === "hi" ? THEME_HI[theme.id]?.title
              : lang === "hi_roman" ? THEME_HI_ROMAN[theme.id]?.title
              : lang === "ta" ? THEME_TA[theme.id]?.title
              : null) || theme.title}
        </button>
        <span style={{ fontSize: "1rem", color: "var(--text-dim)" }}>{verseIdx + 1} / {total}</span>
      </div>

      {/* Verse card */}
      <div style={{
        background: "linear-gradient(135deg, var(--bg2), var(--bg3))",
        border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "28px",
        marginBottom: "20px",
      }}>
        {/* Verse ref badge */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            background: `${theme.color}18`, border: `1px solid ${theme.color}44`,
            borderRadius: "20px", padding: "5px 16px",
            fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", color: darkColor(theme),
          }}>
            {theme.icon} BG {verse.chapter}.{verse.verse}
          </div>
        </div>

        {/* Sanskrit */}
        {verse.sanskrit && (
          <div style={{
            fontFamily: "'Noto Sans Devanagari',serif", fontSize: "1.5rem",
            color: "var(--cream)", lineHeight: "1.9", marginBottom: "10px",
            textShadow: `0 0 24px ${theme.color}55`,
          }}>
            {verse.sanskrit}
          </div>
        )}
        {verse.transliteration && (
          <div style={{ fontStyle: "italic", fontSize: "1.1rem", color: "var(--text-dim)", marginBottom: "20px", lineHeight: "1.7" }}>
            {verse.transliteration}
          </div>
        )}

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "20px 0" }}>
          <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
          <div style={{ width: "8px", height: "8px", background: theme.color, transform: "rotate(45deg)", opacity: 0.7 }} />
          <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
        </div>

        {/* Translation — always English (it's a Sanskrit→English rendering) */}
        {(lang === "hi" || lang === "hi_roman" || lang === "ta" || lang === "ta_roman") && (
          <div style={{ fontSize: "0.9rem", color: "var(--text-dim)", fontWeight: 600,
            marginBottom: "6px" }}>
            {lang === "hi"       ? "संस्कृत अर्थ (अंग्रेज़ी में)"
             : lang === "hi_roman" ? "Sanskrit arth (English mein)"
             : lang === "ta"       ? "சமஸ்கிருத பொருள் (ஆங்கிலத்தில்)"
             : "Sanskrit porul (English-il)"}
          </div>
        )}
        <div style={{ fontSize: "1.3rem", lineHeight: "1.95", color: "var(--text)" }}>
          {verse.meaning}
        </div>
      </div>

      {/* Commentary */}
      {loadingComm && (
        <div style={{ color: "var(--text-dim)", fontSize: "1rem", padding: "20px 0" }}>
          {lang === "hi"       ? "हिंदी टीका लोड हो रही है..."
           : lang === "hi_roman" ? "Hindi teeka load ho rahi hai..."
           : lang === "ta"       ? "தமிழ் உரை ஏற்றுகிறது..."
           : lang === "ta_roman" ? "Tamil urai aetrugiraadhu..."
           : "Loading commentary..."}
        </div>
      )}
      {commentary && !loadingComm && (() => {
        const isHi     = lang === "hi";
        const isHiRom  = lang === "hi_roman";
        const isTa     = lang === "ta";
        const isTaRom  = lang === "ta_roman";
        const L = {
          story:      isHi ? "प्रसंग"  : isHiRom ? "Prasang"   : isTa ? "கதை"         : isTaRom ? "Kadhai"    : "The Story",
          meaning:    isHi ? "अर्थ"    : isHiRom ? "Arth"      : isTa ? "பொருள்"      : isTaRom ? "Porul"     : "What it means for you",
          reflection: isHi ? "विचार"   : isHiRom ? "Vichar"    : isTa ? "சிந்தனை"    : isTaRom ? "Sindhanai" : "Reflection",
        };
        return (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {commentary.story && (
            <div style={{
              background: "linear-gradient(135deg, var(--bg2), var(--bg3))",
              border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "24px",
            }}>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.15rem", color: darkColor(theme), fontWeight: "700", marginBottom: "12px" }}>
                {L.story}
              </div>
              <div style={{ fontSize: "1.2rem", lineHeight: "1.9", color: "var(--text)" }}>{commentary.story}</div>
            </div>
          )}
          {commentary.meaning && (
            <div style={{
              background: "linear-gradient(135deg, var(--bg2), var(--bg3))",
              border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "24px",
            }}>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.15rem", color: darkColor(theme), fontWeight: "700", marginBottom: "12px" }}>
                {L.meaning}
              </div>
              <div style={{ fontSize: "1.2rem", lineHeight: "1.9", color: "var(--text)" }}>{commentary.meaning}</div>
            </div>
          )}
          {commentary.reflection && (
            <div style={{
              background: "linear-gradient(135deg, var(--bg2), var(--bg3))",
              border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "24px",
            }}>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.15rem", color: darkColor(theme), fontWeight: "700", marginBottom: "12px" }}>
                {L.reflection}
              </div>
              <div style={{
                padding: "14px 18px", background: `${theme.color}0d`,
                border: `1px solid ${theme.color}33`, borderRadius: "10px",
                fontStyle: "italic", color: "var(--cream)", fontSize: "1.2rem", lineHeight: "1.8",
              }}>
                🕯️ {commentary.reflection}
              </div>
            </div>
          )}
        </div>
        );
      })()}

      {/* Next / Prev */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "36px", gap: "12px" }}>
        <button onClick={onPrev} disabled={verseIdx === 0} style={{
          flex: 1, padding: "12px", borderRadius: "10px", cursor: verseIdx === 0 ? "default" : "pointer",
          background: "transparent", border: "1px solid var(--border)",
          color: verseIdx === 0 ? "var(--text-dim)" : "var(--text)",
          fontFamily: "'Inter',sans-serif", fontSize: "1rem",
          opacity: verseIdx === 0 ? 0.4 : 1, transition: "all 0.2s",
        }}>
          {lang === "hi" ? "← पिछला" : lang === "hi_roman" ? "← Pichla" : lang === "ta" ? "← முந்தையது" : lang === "ta_roman" ? "← Mundaiyathu" : "← Previous"}
        </button>
        <button onClick={onNext} disabled={verseIdx === total - 1} style={{
          flex: 1, padding: "12px", borderRadius: "10px", cursor: verseIdx === total - 1 ? "default" : "pointer",
          background: verseIdx === total - 1 ? "transparent" : `${theme.color}18`,
          border: `1px solid ${verseIdx === total - 1 ? "var(--border)" : theme.color + "55"}`,
          color: verseIdx === total - 1 ? "var(--text-dim)" : darkColor(theme),
          fontFamily: "'Inter',sans-serif", fontSize: "1rem",
          opacity: verseIdx === total - 1 ? 0.4 : 1, transition: "all 0.2s",
        }}>
          {lang === "hi" ? "अगला →" : lang === "hi_roman" ? "Agla →" : lang === "ta" ? "அடுத்தது →" : lang === "ta_roman" ? "Aduthathu →" : "Next →"}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN BrowsePage
// ══════════════════════════════════════════════════════════════════════════════

const BROWSE_LANG_CHIPS = { en:"EN", hi:"हि", te:"తె", ta:"த", kn:"ಕ", mr:"म" };

// Backend-supplied theme.color values are too pale for text on white; darken per theme for legibility.
const THEME_COLOR_DARK = {
  dharma:       "#a16207",
  fearlessness: "#0f766e",
  liberation:   "#6d28d9",
  devotion:     "#be185d",
  equanimity:   "#15803d",
};
const darkColor = theme => THEME_COLOR_DARK[theme.id] || theme.color;

const THEME_HI = {
  karma:        { title:"कर्म",       subtitle:"बिना फल की चाहत किए काम करो",           desc:"तुम्हारा अधिकार काम पर है, नतीजे पर नहीं — यही गीता की पहली और सबसे गहरी बात है।",     explore:"श्लोक देखो →", verse_tap:"हिंदी उरई देखो →" },
  dharma:       { title:"धर्म",       subtitle:"कर्तव्य, उद्देश्य, सही राह",              desc:"तुम्हारा धर्म दुनिया तय नहीं करती — तुम्हारी आत्मा जानती है।",                       explore:"श्लोक देखो →", verse_tap:"हिंदी उरई देखो →" },
  fearlessness: { title:"निर्भयता",   subtitle:"साहस, भरोसा, डर को पार करना",            desc:"डर से साहस तक — गीता निर्भयता को इनाम नहीं, बल्कि तुम्हारा स्वभाव कहती है।",         explore:"श्लोक देखो →", verse_tap:"हिंदी उरई देखो →" },
  liberation:   { title:"मुक्ति",     subtitle:"मोक्ष, स्वतंत्रता, शाश्वत आत्मा",         desc:"आत्मा अविनाशी है — तुमसे जो छीना नहीं जा सकता, वह क्या है यह समझो।",                explore:"श्लोक देखो →", verse_tap:"हिंदी उरई देखो →" },
  devotion:     { title:"भक्ति",      subtitle:"भक्ति, समर्पण, प्रभु का प्रेम",            desc:"सीधा रास्ता — शुद्ध प्रेम से सब कुछ भगवान को अर्पण करो।",                           explore:"श्लोक देखो →", verse_tap:"हिंदी उरई देखो →" },
  equanimity:   { title:"समता",       subtitle:"शांति, समभाव, स्थिर मन",                  desc:"स्थितप्रज्ञ — दुख में भी, सुख में भी — जो मन नहीं डोलता।",                          explore:"श्लोक देखो →", verse_tap:"हिंदी उरई देखो →" },
};

const THEME_HI_ROMAN = {
  karma:        { title:"Karma",       subtitle:"Bina phal ki chaahat kiye kaam karo",    desc:"Tumhara adhikaar kaam par hai, natije par nahi — yahi Gita ki pehli aur sabse gehri baat hai.",  explore:"Shlok dekho →", verse_tap:"Roman Hindi urai dekho →" },
  dharma:       { title:"Dharma",      subtitle:"Kartavya, uddeshya, sahi raah",           desc:"Tumhara dharma duniya tay nahi karti — tumhari aatma jaanti hai.",                               explore:"Shlok dekho →", verse_tap:"Roman Hindi urai dekho →" },
  fearlessness: { title:"Nirbhayata",  subtitle:"Sahas, bharosa, dar ko paar karna",       desc:"Dar se sahas tak — Gita nirbhayata ko inaam nahi, balki tumhara svabhaav kehti hai.",            explore:"Shlok dekho →", verse_tap:"Roman Hindi urai dekho →" },
  liberation:   { title:"Mukti",       subtitle:"Moksha, swatantrata, shaashvat aatma",    desc:"Aatma avinaashi hai — tumse jo cheena nahi ja sakta, voh kya hai yeh samjho.",                   explore:"Shlok dekho →", verse_tap:"Roman Hindi urai dekho →" },
  devotion:     { title:"Bhakti",      subtitle:"Bhakti, samarpan, Prabhu ka prem",        desc:"Seedha raasta — shudh prem se sab kuch Bhagwan ko arpan karo.",                                  explore:"Shlok dekho →", verse_tap:"Roman Hindi urai dekho →" },
  equanimity:   { title:"Samata",      subtitle:"Shaanti, samabhaav, sthir man",            desc:"Sthitapragya — dukh mein bhi, sukh mein bhi — jo man nahi dolata.",                              explore:"Shlok dekho →", verse_tap:"Roman Hindi urai dekho →" },
};

const THEME_TA = {
  karma:        { title:"கர்மா",       subtitle:"பற்றின்றி செய்யும் செயல்",              desc:"உன் கடமையை செய். பலனை விட்டுவிடு — இதுவே கீதையின் முதல் பாடம்.",          explore:"வசனங்கள் காண →", verse_tap:"தமிழ் உரை காண →" },
  dharma:       { title:"தர்மம்",      subtitle:"கடமை, நோக்கம், நீதியான பாதை",           desc:"உன் தர்மம் உலகம் சொல்வதில்லை — உன் ஆத்மா அறியும்.",                     explore:"வசனங்கள் காண →", verse_tap:"தமிழ் உரை காண →" },
  fearlessness: { title:"அச்சமின்மை",  subtitle:"தைரியம், நம்பிக்கை, அச்சத்தை வெல்வது",   desc:"திகிலிலிருந்து தைரியம் வரை — கீதை அச்சத்தைப் பற்றி என்ன சொல்கிறது.",      explore:"வசனங்கள் காண →", verse_tap:"தமிழ் உரை காண →" },
  liberation:   { title:"முக்தி",      subtitle:"மோட்சம், சுதந்திரம், நித்திய ஆத்மா",     desc:"ஆத்மா அழியாதது — உன்னிடமிருந்து எடுக்க முடியாதது என்னவென்று புரிந்துகொள்.", explore:"வசனங்கள் காண →", verse_tap:"தமிழ் உரை காண →" },
  devotion:     { title:"பக்தி",       subtitle:"பக்தி, சரணாகதி, இறை அன்பு",             desc:"நேரடி வழி — தூய அன்புடன் எல்லாவற்றையும் இறைவனுக்கு அர்ப்பணி.",           explore:"வசனங்கள் காண →", verse_tap:"தமிழ் உரை காண →" },
  equanimity:   { title:"சமநிலை",     subtitle:"அமைதி, சமநிலை, உறுதியான மனம்",          desc:"ஸ்திதப்ரஜ்ஞன் — துக்கத்திலும் சுகத்திலும் அசையாத மனம் கொண்டவன்.",        explore:"வசனங்கள் காண →", verse_tap:"தமிழ் உரை காண →" },
};
const THEME_TA_ROMAN = {
  karma:        { title:"Karma",       subtitle:"Patrindri seyyum seyal",               desc:"Un kadamaiyai sey. Palaanai vittuvi — iduve Geetaiyin mudhal paadam.",         explore:"Vasanangal kaan →", verse_tap:"Tamil urai kaan →" },
  dharma:       { title:"Dharmam",     subtitle:"Kadamai, nokkam, neetiyana paadai",    desc:"Un dharmam ulagam solvadhillai — un aatma ariyum.",                          explore:"Vasanangal kaan →", verse_tap:"Tamil urai kaan →" },
  fearlessness: { title:"Achamainmai", subtitle:"Thairiyam, nambikkai, achathai velvadhu", desc:"Thigilidirundhu thairiyam varai — Geetai achathai pattri enna sollugiraadhu.", explore:"Vasanangal kaan →", verse_tap:"Tamil urai kaan →" },
  liberation:   { title:"Mukthi",      subtitle:"Moksham, sudhandram, nithiya aatma",   desc:"Aatma azhiyaadhadhu — unnidamirundhu edukka mudiyaadhadhu enna endru purindhu ko.", explore:"Vasanangal kaan →", verse_tap:"Tamil urai kaan →" },
  devotion:     { title:"Bhakthi",     subtitle:"Bhakthi, saranagathi, irai anbu",      desc:"Nerada vazhi — thooiya anbhudan ellaavattraiyum iraivanukkku arpanam sey.",    explore:"Vasanangal kaan →", verse_tap:"Tamil urai kaan →" },
  equanimity:   { title:"Samanilai",   subtitle:"Amaidhi, samanilai, uruthiyaana manam",desc:"Sthithapragyan — thukkathilum sugathilum asaiyaadha manam kondavan.",          explore:"Vasanangal kaan →", verse_tap:"Tamil urai kaan →" },
};

export default function BrowsePage({ appLang, appScript, onLangChange }) {
  const [view, setView]               = useState("grid");
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [verseIdx, setVerseIdx]       = useState(0);

  const baseLang = appLang || localStorage.getItem("app_lang") || "en";
  const script   = appScript || localStorage.getItem("jyotish_script_pref") || "native";
  // Hindi and Tamil have native commentary; both support roman/native script toggle
  const lang     = baseLang === "hi" ? (script === "roman" ? "hi_roman" : "hi")
                 : baseLang === "ta" ? (script === "roman" ? "ta_roman" : "ta")
                 : "en";

  const changeLang   = (l) => onLangChange?.(l, l === "en" ? "native" : script);
  const changeScript = (s) => onLangChange?.(baseLang, s);

  return (
    <div className="page">

      {/* Language chips — same style as JyotishPage */}
      <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"18px", alignItems:"center" }}>
        {["en","hi","te","ta","kn","mr"].map(l => (
          <button key={l} onClick={() => changeLang(l)} style={{
            background: baseLang === l ? "var(--saffron)" : "var(--bg2)",
            color:      baseLang === l ? "#0f0e0d"        : "var(--text-dim)",
            border:     `1px solid ${baseLang === l ? "var(--saffron)" : "var(--border)"}`,
            borderRadius:"20px", padding:"5px 14px",
            fontFamily:"'Inter',sans-serif", fontSize:"0.95rem",
            fontWeight: baseLang === l ? 700 : 400, cursor:"pointer",
            transition:"all 0.15s",
            boxShadow: baseLang === l ? "0 2px 10px rgba(232,131,42,0.3)" : "none",
            opacity: l !== "en" && l !== "ta" && l !== "hi" ? 0.45 : 1,
          }}>
            {BROWSE_LANG_CHIPS[l]}
          </button>
        ))}
        {(baseLang === "ta" || baseLang === "hi") && (
          <div style={{ display:"flex", borderRadius:10, overflow:"hidden",
            border:"1px solid var(--border)", marginLeft:"auto" }}>
            {[["roman","Aa"], ["native", baseLang === "hi" ? "ह" : "த"]].map(([val, label]) => (
              <button key={val} onClick={() => changeScript(val)} style={{
                padding:"5px 14px", fontSize:"0.95rem", border:"none", cursor:"pointer",
                background: script === val ? "rgba(232,131,42,0.18)" : "transparent",
                color:      script === val ? "var(--saffron)"         : "var(--text-dim)",
                transition:"all 0.15s",
              }}>{label}</button>
            ))}
          </div>
        )}
      </div>

      {view === "grid" && (
        <ThemeGrid lang={lang} onSelect={theme => { setSelectedTheme(theme); setView("list"); }} />
      )}
      {view === "list" && selectedTheme && (
        <ThemeVerseList
          themeId={selectedTheme.id}
          lang={lang}
          onBack={() => setView("grid")}
          onOpenVerse={(theme, idx) => { setSelectedTheme(theme); setVerseIdx(idx); setView("verse"); }}
        />
      )}
      {view === "verse" && selectedTheme && (
        <ThemeVerseDetail
          theme={selectedTheme}
          verseIdx={verseIdx}
          lang={lang}
          onBack={() => setView("list")}
          onNext={() => setVerseIdx(i => i + 1)}
          onPrev={() => setVerseIdx(i => i - 1)}
        />
      )}
    </div>
  );
}
